import { FieldTitle } from './RedStarTitle';
import Select from 'react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { FieldDesc } from './common';
import {
  TextField,
  Switch,
  FormControlLabel,
} from '@client/new/components/basic';
import { OfferDetailsData } from './OfferDetails.mappers';
import { TogglableButtons } from '@design-system/components/togglableButtons';
import {
  WorkoutsDropDownMultiselect,
  WorkoutThumbnail,
} from './WorkoutsDropDownMultiselect';
import { rgb } from '@design-system/tokens';
import { useCustomizations } from '@client/new/hooks/use-customizations';

export function OfferDetailsSection({
  index,
  influencerId,
  offerData,
  onWorkoutClick,
}: {
  index: number;
  influencerId: number;
  offerData?: OfferDetailsData;
  onWorkoutClick?: (workoutId: number, programId?: number | null) => void;
}) {
  const { register, control, watch, setValue } = useFormContext();
  const r = (s: string) => register(`sections.${index}.${s}`);
  const programOptions = offerData?.programIds.options || [];
  const workoutOptions = offerData?.workoutIds.options || [];
  const extrasOptions = offerData?.extrasIds.options || [];
  const communityFeatureOptions = offerData?.communityFeatures.options || [];

  const { customizations } = useCustomizations(String(influencerId));
  const isOffers35Enabled = customizations?.isOffers35Enabled === true;

  const selectedProgramIds: number[] =
    watch(`sections.${index}.programIds`) || [];

  // Group workouts by their program (program name as group label), limited to
  // the programs currently selected in the form.
  const groupsByProgram = new Map<
    number | null,
    { label: string; options: typeof workoutOptions }
  >();
  for (const workout of workoutOptions) {
    if (
      workout.programId == null ||
      !selectedProgramIds.includes(workout.programId)
    ) {
      continue;
    }
    const group = groupsByProgram.get(workout.programId);
    if (group) {
      group.options.push(workout);
    } else {
      groupsByProgram.set(workout.programId, {
        label: workout.programName,
        options: [workout],
      });
    }
  }
  const groupedWorkoutOptions = Array.from(groupsByProgram.values());

  const selectedWorkoutIds: number[] =
    watch(`sections.${index}.workoutIds`) || [];

  const workoutsPreviewEnabled = !!watch(
    `sections.${index}.workoutsPreviewEnabled`,
  );

  // When the preview is enabled, default to the first 5 workouts of each
  // selected program (only when nothing is selected yet, to preserve a saved
  // selection).
  const handleToggleWorkoutsPreview = (enabled: boolean) => {
    setValue(`sections.${index}.workoutsPreviewEnabled`, enabled, {
      shouldDirty: true,
    });
    if (!enabled || selectedWorkoutIds.length > 0) return;

    const defaultWorkoutIds = selectedProgramIds.flatMap((programId) =>
      workoutOptions
        .filter((w) => w.programId === programId)
        .slice(0, 5)
        .map((w) => w.value),
    );
    if (defaultWorkoutIds.length > 0) {
      setValue(`sections.${index}.workoutIds`, defaultWorkoutIds, {
        shouldDirty: true,
      });
    }
  };

  const handleProgramsChange = (programIds: number[]) => {
    setValue(`sections.${index}.programIds`, programIds, { shouldDirty: true });

    // Drop selected workouts whose program is no longer selected.
    const allowed = selectedWorkoutIds.filter((id) => {
      const workout = workoutOptions.find((w) => w.value === id);
      return (
        workout?.programId != null && programIds.includes(workout.programId)
      );
    });
    if (allowed.length !== selectedWorkoutIds.length) {
      setValue(`sections.${index}.workoutIds`, allowed, { shouldDirty: true });
    }
  };

  return (
    <div className="w-100">
      <div className="max-w-[680px]">
        {/* 1. Headline Text */}
        <div>
          <FieldTitle title={'Headline text'} />
          <FieldDesc>
            The headline is used to communicate the offer&apos;s value
            proposition.
          </FieldDesc>
          <TextField
            {...r(`headlineText`)}
            className="mb-[5px] max-w-[680px] w-full"
            inputProps={{
              placeholder: 'Headline text',
              maxLength: 60,
            }}
          />
        </div>

        {/* 2. Programs Multi-Select */}
        {programOptions.length > 0 && (
          <div className="mt-8">
            <FieldTitle title={'Choose Programs'} optional />
            <FieldDesc>
              Select the programs included in this offer. They will be displayed
              in the order selected.
            </FieldDesc>
            <Controller
              name={`sections.${index}.programIds`}
              control={control}
              render={({ field: { value, ref } }) => (
                <Select
                  ref={ref}
                  isMulti
                  options={programOptions}
                  value={(value || [])
                    .map((id: number) =>
                      programOptions.find((p) => p.value === id),
                    )
                    .filter(Boolean)}
                  onChange={(selected) =>
                    handleProgramsChange(selected?.map((s) => s.value) || [])
                  }
                  placeholder="Select programs..."
                />
              )}
            />
          </div>
        )}
        {/* 3. Workouts preview */}
        {isOffers35Enabled && workoutOptions.length > 0 && (
          <div className="mt-8 flex flex-col gap-3">
            <div className="flex items-center">
              <FieldTitle title={'Workouts preview'} optional />
              <FormControlLabel
                control={
                  <Switch
                    className="ml-5"
                    checked={workoutsPreviewEnabled}
                    onChange={(e) =>
                      handleToggleWorkoutsPreview(e.target.checked)
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: rgb('P-80'),
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: rgb('P-80'),
                        },
                    }}
                  />
                }
                label=""
              />
            </div>

            {workoutsPreviewEnabled && (
              <>
                <FieldDesc>
                  If this is on, Programs selected become clickable and workouts
                  preview will display by default thumbnails for first 5
                  workouts in the program. Will be displayed in order selected.
                </FieldDesc>
                {selectedWorkoutIds.length === 0 && (
                  <div className="text-red-500 text-sm">
                    Select at least one workout or disable the workouts preview.
                  </div>
                )}
                <Controller
                  name={`sections.${index}.workoutIds`}
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const selectedIds: number[] = value || [];
                    const selectedWorkouts = selectedIds
                      .map((id) => workoutOptions.find((w) => w.value === id))
                      .filter(Boolean) as typeof workoutOptions;

                    // Group the selected workouts by program, preserving the
                    // selection order.
                    const selectedByProgram = new Map<
                      number | null,
                      typeof selectedWorkouts
                    >();
                    for (const workout of selectedWorkouts) {
                      const group = selectedByProgram.get(workout.programId);
                      if (group) {
                        group.push(workout);
                      } else {
                        selectedByProgram.set(workout.programId, [workout]);
                      }
                    }

                    return (
                      <>
                        <WorkoutsDropDownMultiselect
                          groups={groupedWorkoutOptions}
                          value={selectedIds}
                          onChange={onChange}
                        />

                        {selectedByProgram.size > 0 && (
                          <div className="flex flex-col gap-4 mt-3">
                            <FieldDesc textClassName="text-[#333] text-m">
                              Workout thumbnails
                            </FieldDesc>
                            {Array.from(selectedByProgram.entries()).map(
                              ([programId, workouts]) => (
                                <div key={String(programId)}>
                                  <p className="text-m font-semibold mb-4">
                                    {workouts[0].programName}
                                  </p>
                                  <div className="flex flex-wrap gap-3">
                                    {workouts.map((workout) => (
                                      <div
                                        key={workout.value}
                                        className="relative"
                                      >
                                        <div
                                          className="cursor-pointer"
                                          title={`Open ${workout.label}`}
                                          onClick={() =>
                                            onWorkoutClick?.(
                                              workout.value,
                                              workout.programId,
                                            )
                                          }
                                        >
                                          <WorkoutThumbnail
                                            imgUrl={workout.imgUrl}
                                            alt={workout.label}
                                            className="w-[100px] h-[160px] rounded"
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          aria-label={`Remove ${workout.label}`}
                                          onClick={() =>
                                            onChange(
                                              selectedIds.filter(
                                                (id) => id !== workout.value,
                                              ),
                                            )
                                          }
                                          className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-black text-white text-xs leading-none"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </>
                    );
                  }}
                />
              </>
            )}
          </div>
        )}

        {/* 4. Extras Multi-Select */}
        {extrasOptions.length > 0 && (
          <div className="mt-8">
            <FieldTitle title={'Choose Extras'} optional />
            <FieldDesc>
              Select the extra content items included in this offer.
            </FieldDesc>
            <Controller
              name={`sections.${index}.extrasIds`}
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <Select
                  ref={ref}
                  isMulti
                  options={extrasOptions}
                  value={(value || [])
                    .map((id: string) =>
                      extrasOptions.find((p) => p.value === id),
                    )
                    .filter(Boolean)}
                  onChange={(selected) =>
                    onChange(selected?.map((s) => s.value) || [])
                  }
                  placeholder="Select extras..."
                />
              )}
            />
          </div>
        )}

        {/* 5. Community Features Togglable Buttons */}
        {communityFeatureOptions.length > 0 && (
          <div className="mt-8">
            <FieldTitle title={'Community Features'} optional />
            <FieldDesc>
              Select the community features included in this offer.
            </FieldDesc>
            <Controller
              name={`sections.${index}.communityFeatures`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <TogglableButtons
                  options={communityFeatureOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  value={value || []}
                  onChange={onChange}
                />
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
